import React, { useMemo, useState } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import styled from "styled-components";
import { pluralize } from "utils/admin";
import { formatPortalLabel } from "utils/portal-access";
import { EmptyState } from "./ui";
import {
  RoleOrgChartSecondaryButton,
  RoleOrgChartSectionTertiary,
  RoleOrgChartContentStack,
  RoleOrgChartHeaderRow,
  RoleOrgChartSectionQuaternary,
  RoleOrgChartScrollArea,
  RoleOrgChartSectionBody,
  RoleOrgChartSectionQuinary,
  RoleOrgChartSectionSenary,
  RoleOrgChartSection,
  RoleOrgChartHeaderRowSecondary,
  RoleOrgChartSectionSecondary,
  RoleOrgChartPanelCard,
  RoleOrgChartIllustrationImage,
  RoleOrgChartActionLink,
  RoleOrgChartMetaText,
  RoleOrgChartBodyTextTertiary,
  RoleOrgChartBodyText,
  RoleOrgChartEyebrowText,
  RoleOrgChartBodyTextSecondary,
  RoleOrgChartStatusBadge,
  RoleOrgChartInlineText,
} from "components/admin/styles/role-org-chart.inline.styles";
type RoleOrgChartProps = {
  assignments: any[];
  users?: any[];
  schoolName?: string;
  schoolId?: string;
};
type OrgNode = {
  key: string;
  title: string;
  subtitle: string;
  meta?: string;
  imageSrc?: string;
  fallbackLabel?: string;
  synthetic?: boolean;
  userId?: string;
  roleKey?: string;
};
type OrgBranch = {
  key: string;
  topNodes: OrgNode[];
  childNodes: OrgNode[];
};
type OrgTreeItem = {
  key: string;
  node: OrgNode;
  children: OrgTreeItem[];
};
type RolePalette = {
  badgeBackground: string;
  badgeText: string;
  border: string;
  stripe: string;
};
const roleMeta: Record<
  string,
  {
    badge: string;
    label: string;
    order: number;
    palette: {
      badgeBackground: string;
      badgeText: string;
      border: string;
      stripe: string;
    };
  }
> = {
  head_of_sports: {
    badge: "Head",
    label: "Head of Sports",
    order: 0,
    palette: {
      badgeBackground: "rgba(240, 191, 86, 0.14)",
      badgeText: "#f0bf56",
      border: "rgba(240, 191, 86, 0.2)",
      stripe: "#d3a63c",
    },
  },
  head_of_sport: {
    badge: "Lead",
    label: "Head of Sport",
    order: 1,
    palette: {
      badgeBackground: "rgba(214, 179, 109, 0.14)",
      badgeText: "#f3ddb0",
      border: "rgba(214, 179, 109, 0.2)",
      stripe: "#b2873c",
    },
  },
  coach: {
    badge: "Coach",
    label: "Coach",
    order: 2,
    palette: {
      badgeBackground: "rgba(180, 83, 9, 0.16)",
      badgeText: "#f6d69a",
      border: "rgba(180, 83, 9, 0.2)",
      stripe: "#a86e21",
    },
  },
  brand_ambassador: {
    badge: "Brand",
    label: "Brand Ambassador",
    order: 3,
    palette: {
      badgeBackground: "rgba(159, 58, 78, 0.16)",
      badgeText: "#fecdd3",
      border: "rgba(159, 58, 78, 0.2)",
      stripe: "#9f3a4e",
    },
  },
  synthetic: {
    badge: "Scope",
    label: "School Structure",
    order: 99,
    palette: {
      badgeBackground: "rgba(240, 191, 86, 0.14)",
      badgeText: "#f0bf56",
      border: "rgba(240, 191, 86, 0.2)",
      stripe: "#d3a63c",
    },
  },
};
const AvatarInitialsSurface = styled.div<{
  $synthetic: boolean;
}>`
  align-items: center;
  background: ${({ $synthetic }) =>
    $synthetic ? "rgba(99, 70, 28, 0.92)" : "var(--brand-highlight)"};
  color: white;
  display: flex;
  font-size: 1.125rem;
  font-weight: 600;
  height: 100%;
  justify-content: center;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  width: 100%;
`;
const RoleCardSurface = styled.div<{
  $interactive: boolean;
  $palette: RolePalette;
}>`
  background: rgba(30, 35, 43, 0.98);
  border-color: ${({ $palette }) => $palette.border};
  border-top-color: ${({ $palette }) => $palette.stripe};
  border-top-width: 4px;
  box-shadow: none;

  ${({ $interactive }) =>
    $interactive
      ? `
        &:hover {
          box-shadow: none;
          transform: translateY(-0.125rem);
        }
      `
      : ""}
`;
const RoleBadgePill = styled.span<{
  $palette: RolePalette;
}>`
  background: ${({ $palette }) => $palette.badgeBackground};
  color: ${({ $palette }) => $palette.badgeText};
`;
const sortAssignments = (left: any, right: any) => {
  const leftOrder = roleMeta[left?.role]?.order ?? 99;
  const rightOrder = roleMeta[right?.role]?.order ?? 99;
  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }
  return String(left?.user_name || "").localeCompare(String(right?.user_name || ""));
};
const resolveUserImage = (assignment: any, users: Record<string, any>) => {
  const userId = String(assignment?.user_id ?? "");
  const linkedUser = users[userId];
  const assignmentUser = assignment?.user || assignment?.user_profile || assignment?.person || {};
  return (
    assignment?.user_image_url ||
    assignment?.profile_image_url ||
    assignmentUser?.profile_image_url ||
    assignmentUser?.user_image_url ||
    assignmentUser?.image_url ||
    assignmentUser?.image ||
    assignmentUser?.avatar_url ||
    assignmentUser?.avatar ||
    assignment?.image_url ||
    assignment?.avatar_url ||
    linkedUser?.user_image_url ||
    linkedUser?.profile_image_url ||
    linkedUser?.avatar_url ||
    linkedUser?.image_url ||
    linkedUser?.image ||
    linkedUser?.avatar ||
    ""
  );
};
const createAssignmentNode = (assignment: any, userLookup: Record<string, any>): OrgNode => {
  const roleKey = String(assignment?.role || "synthetic");
  return {
    fallbackLabel: assignment?.user_name || roleMeta[roleKey]?.label || "Assignment",
    imageSrc: resolveUserImage(assignment, userLookup),
    key: `role-${assignment?.id ?? assignment?.role ?? Math.random()}`,
    meta:
      assignment?.sport_name ||
      (roleKey === "head_of_sports"
        ? "School-wide"
        : roleKey === "brand_ambassador"
        ? "Community"
        : "General"),
    roleKey,
    subtitle: roleMeta[roleKey]?.label || formatPortalLabel(roleKey),
    title: assignment?.user_name || "Unassigned",
    userId: assignment?.user_id ? String(assignment.user_id) : undefined,
  };
};
const createSyntheticNode = (
  key: string,
  title: string,
  subtitle: string,
  meta?: string,
): OrgNode => ({
  fallbackLabel: title,
  key,
  meta,
  roleKey: "synthetic",
  subtitle,
  synthetic: true,
  title,
});
const AvatarBadge: React.FC<{
  imageSrc?: string;
  label: string;
  synthetic?: boolean;
}> = ({ imageSrc, label, synthetic = false }) => {
  if (imageSrc) {
    return <RoleOrgChartIllustrationImage alt={label} loading="lazy" src={imageSrc} />;
  }
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("");
  return <AvatarInitialsSurface $synthetic={synthetic}>{initials || "AT"}</AvatarInitialsSurface>;
};
const RoleNodeCard: React.FC<{
  node: OrgNode;
  schoolId?: string;
}> = ({ node, schoolId }) => {
  const tone = roleMeta[node.roleKey || "synthetic"] || roleMeta.synthetic;
  const isClickable = Boolean(node.userId && !node.synthetic);
  const content = (
    <RoleCardSurface
      className={`org-node-card group relative mx-auto flex flex-col overflow-hidden rounded-[18px] border bg-[rgba(24,29,36,0.96)] px-3.5 pb-4 pt-3 text-center transition ${
        isClickable ? "hover:-translate-y-0.5 hover:shadow-[0_18px_28px_rgba(0,0,0,0.28)]" : ""
      }`}
      $interactive={isClickable}
      $palette={tone.palette}
    >
      <RoleOrgChartSection>
        <RoleBadgePill
          className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-[0.14em]"
          $palette={tone.palette}
        >
          {tone.badge}
        </RoleBadgePill>
      </RoleOrgChartSection>

      <RoleOrgChartSectionSecondary>
        <RoleOrgChartPanelCard>
          <AvatarBadge
            imageSrc={node.imageSrc}
            label={node.fallbackLabel || node.title}
            synthetic={node.synthetic}
          />
        </RoleOrgChartPanelCard>

        <RoleOrgChartBodyText>{node.title}</RoleOrgChartBodyText>
        <RoleOrgChartEyebrowText>{node.subtitle}</RoleOrgChartEyebrowText>
        <RoleOrgChartBodyTextSecondary>{node.meta || "General"}</RoleOrgChartBodyTextSecondary>

        {isClickable ? <RoleOrgChartInlineText>View profile</RoleOrgChartInlineText> : null}
      </RoleOrgChartSectionSecondary>
    </RoleCardSurface>
  );
  if (!isClickable) {
    return content;
  }
  return (
    <RoleOrgChartActionLink
      aria-label={`View ${node.title} profile`}
      to={{
        pathname: `/workspace/school-roles/users/${node.userId}`,
        state: {
          backTo: "/workspace/school?tab=staff",
          schoolId,
        },
      }}
    >
      {content}
    </RoleOrgChartActionLink>
  );
};
const MobileTreeItem: React.FC<{
  item: OrgTreeItem;
  nested?: boolean;
  schoolId?: string;
}> = ({ item, nested = false, schoolId }) => (
  <div className={`org-mobile-item${nested ? " is-nested" : ""}`}>
    <RoleNodeCard node={item.node} schoolId={schoolId} />
    {item.children.length > 0 ? (
      <RoleOrgChartSectionTertiary>
        {item.children.map((child) => (
          <MobileTreeItem item={child} key={child.key} nested schoolId={schoolId} />
        ))}
      </RoleOrgChartSectionTertiary>
    ) : null}
  </div>
);
const renderDesktopTreeNode = (item: OrgTreeItem, schoolId?: string): React.ReactElement => (
  <TreeNode key={item.key} label={<RoleNodeCard node={item.node} schoolId={schoolId} />}>
    {item.children.map((child) => renderDesktopTreeNode(child, schoolId))}
  </TreeNode>
);
export const RoleOrgChart: React.FC<RoleOrgChartProps> = ({
  assignments,
  schoolName = "School",
  schoolId,
  users = [],
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const activeAssignments = useMemo(
    () =>
      (assignments || [])
        .filter((assignment) => assignment?.active !== false)
        .sort(sortAssignments),
    [assignments],
  );
  const userLookup = useMemo(
    () => Object.fromEntries((users || []).map((user: any) => [String(user.id), user])),
    [users],
  );
  const leadershipAssignments = activeAssignments.filter(
    (assignment) => assignment.role === "head_of_sports",
  );
  const ambassadorAssignments = activeAssignments.filter(
    (assignment) => assignment.role === "brand_ambassador",
  );
  const headOfSportAssignments = activeAssignments.filter(
    (assignment) => assignment.role === "head_of_sport",
  );
  const coachAssignments = activeAssignments.filter((assignment) => assignment.role === "coach");
  const directBranches = useMemo(() => {
    const sportBranchLookup = new Map<string, OrgBranch>();
    headOfSportAssignments.forEach((assignment) => {
      const sportKey = String(assignment.sport_id || assignment.sport_name || assignment.id);
      const existing = sportBranchLookup.get(sportKey) || {
        childNodes: [],
        key: `sport-${sportKey}`,
        topNodes: [],
      };
      existing.topNodes.push(createAssignmentNode(assignment, userLookup));
      sportBranchLookup.set(sportKey, existing);
    });
    coachAssignments.forEach((assignment) => {
      const sportKey = String(assignment.sport_id || assignment.sport_name || assignment.id);
      const existing = sportBranchLookup.get(sportKey) || {
        childNodes: [],
        key: `sport-${sportKey}`,
        topNodes: [
          createSyntheticNode(
            `sport-anchor-${sportKey}`,
            assignment.sport_name || "Sport Programme",
            "Programme",
            "Coach Group",
          ),
        ],
      };
      existing.childNodes.push(createAssignmentNode(assignment, userLookup));
      sportBranchLookup.set(sportKey, existing);
    });
    const sportBranches = Array.from(sportBranchLookup.entries())
      .sort((left, right) => left[0].localeCompare(right[0]))
      .map(([, branch]) => branch);
    const ambassadorBranches = ambassadorAssignments.map((assignment) => ({
      childNodes: [],
      key: `ambassador-${assignment.id}`,
      topNodes: [createAssignmentNode(assignment, userLookup)],
    }));
    return [...sportBranches, ...ambassadorBranches];
  }, [ambassadorAssignments, coachAssignments, headOfSportAssignments, userLookup]);
  const tree = useMemo(() => {
    const branchItems: OrgTreeItem[] = directBranches.map((branch) => {
      if (branch.topNodes.length === 1) {
        return {
          children: branch.childNodes.map((child) => ({
            children: [],
            key: child.key,
            node: child,
          })),
          key: branch.key,
          node: branch.topNodes[0],
        };
      }
      return {
        children: [...branch.topNodes, ...branch.childNodes].map((child) => ({
          children: [],
          key: child.key,
          node: child,
        })),
        key: branch.key,
        node: createSyntheticNode(
          `branch-anchor-${branch.key}`,
          branch.topNodes[0]?.meta || schoolName,
          "Leadership Group",
          pluralize(branch.topNodes.length, "lead"),
        ),
      };
    });
    if (leadershipAssignments.length === 1) {
      return {
        children: branchItems,
        key: `root-${leadershipAssignments[0].id || "leadership"}`,
        node: createAssignmentNode(leadershipAssignments[0], userLookup),
      } satisfies OrgTreeItem;
    }
    if (leadershipAssignments.length > 1) {
      return {
        children: [
          ...leadershipAssignments.map((assignment) => ({
            children: [],
            key: `lead-${assignment.id || assignment.user_id}`,
            node: createAssignmentNode(assignment, userLookup),
          })),
          ...branchItems,
        ],
        key: `root-${schoolName}`,
        node: createSyntheticNode(
          `school-root-${schoolName}`,
          schoolName,
          "School Structure",
          pluralize(leadershipAssignments.length, "lead"),
        ),
      } satisfies OrgTreeItem;
    }
    if (branchItems.length > 0) {
      return {
        children: branchItems,
        key: `root-${schoolName}`,
        node: createSyntheticNode(
          `school-root-${schoolName}`,
          schoolName,
          "School Structure",
          pluralize(branchItems.length, "active branch"),
        ),
      } satisfies OrgTreeItem;
    }
    return null;
  }, [directBranches, leadershipAssignments, schoolName, userLookup]);
  if (!activeAssignments.length || !tree) {
    return (
      <EmptyState
        description="Active role assignments will form a live school structure here once leadership and coaching roles are mapped."
        title="No active structure yet"
      />
    );
  }
  return (
    <RoleOrgChartContentStack>
      <RoleOrgChartHeaderRow>
        <RoleOrgChartSectionQuaternary>
          <RoleOrgChartStatusBadge>
            {pluralize(activeAssignments.length, "active role")}
          </RoleOrgChartStatusBadge>
          <RoleOrgChartStatusBadge>
            {pluralize(headOfSportAssignments.length, "sport lead")}
          </RoleOrgChartStatusBadge>
          <RoleOrgChartStatusBadge>
            {pluralize(coachAssignments.length, "coach")}
          </RoleOrgChartStatusBadge>
          <RoleOrgChartStatusBadge>
            {pluralize(ambassadorAssignments.length, "ambassador")}
          </RoleOrgChartStatusBadge>
        </RoleOrgChartSectionQuaternary>

        <RoleOrgChartSecondaryButton
          onClick={() => setIsVisible((current) => !current)}
          type="button"
        >
          {isVisible ? "Hide tree" : "Show tree"}
        </RoleOrgChartSecondaryButton>
      </RoleOrgChartHeaderRow>

      {isVisible ? (
        <>
          <RoleOrgChartScrollArea>
            <RoleOrgChartSectionBody>
              <RoleOrgChartSectionQuinary>
                <Tree
                  label={<RoleNodeCard node={tree.node} schoolId={schoolId} />}
                  lineBorderRadius="18px"
                  lineColor="rgba(240, 191, 86, 0.68)"
                  lineHeight="28px"
                  lineWidth="4px"
                  nodePadding="18px"
                >
                  {tree.children.map((child) => renderDesktopTreeNode(child, schoolId))}
                </Tree>
              </RoleOrgChartSectionQuinary>
            </RoleOrgChartSectionBody>
          </RoleOrgChartScrollArea>

          <RoleOrgChartSectionSenary>
            <MobileTreeItem item={tree} schoolId={schoolId} />
          </RoleOrgChartSectionSenary>
        </>
      ) : (
        <RoleOrgChartHeaderRowSecondary>
          <RoleOrgChartMetaText>
            The organisation tree is hidden. Show it again to review reporting lines and open staff
            profiles.
          </RoleOrgChartMetaText>
        </RoleOrgChartHeaderRowSecondary>
      )}

      <RoleOrgChartBodyTextTertiary>
        Expand the tree to review reporting lines, then open a profile card for public staff
        details. Inactive assignments stay in the assignments table so the chart stays readable.
      </RoleOrgChartBodyTextTertiary>
    </RoleOrgChartContentStack>
  );
};
